import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { FormInput } from '../components/common/FormFields';
import { apiClient } from '../api/client';

interface InvitationData {
  email: string;
  fullName: string;
  tenantName: string;
  roles: string[];
  userId: string;
  tenantId: string;
}

export const InviteAcceptancePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Verify invitation token
    verifyInvitation();
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await apiClient.post('/auth/verify-invitation', { token });
      setInvitationData(response.data.invitation);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/auth/accept-invitation', {
        token,
        password
      });

      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to activate account');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="h-screen overflow-hidden">
        <div className="flex h-full">
          {/* Left Panel */}
          <div className="w-1/2 bg-slate-700 text-white px-12 py-8 flex flex-col justify-center relative lg:px-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg font-bold relative">
                <span>T</span>
                <div className="absolute top-2.5 left-2.5 right-2.5 bottom-2.5 border-2 border-white rounded-full border-t-transparent border-l-transparent transform -rotate-45"></div>
              </div>
              <div className="text-2xl font-bold">METATALIO</div>
            </div>
            
            <h1 className="text-3xl font-bold mb-4">Join MetaTalio Today</h1>
            <p className="text-base text-slate-300 mb-8">Streamline your recruitment process with intelligent candidate tracking and management</p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Track candidates in real-time</h3>
                  <p className="text-slate-300 text-sm">Monitor every stage of your recruitment pipeline</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">AI-powered insights</h3>
                  <p className="text-slate-300 text-sm">Make data-driven hiring decisions</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Seamless collaboration</h3>
                  <p className="text-slate-300 text-sm">Connect with partners effortlessly</p>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-6 left-12 text-xs text-slate-500 lg:left-16">
              © 2024 MetaTalio. All rights reserved.
            </div>
          </div>
          
          {/* Right Panel */}
          <div className="w-1/2 bg-white px-8 py-8 flex flex-col justify-center">
            <div className="max-w-sm mx-auto w-full text-center">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
              <p className="text-slate-600 mb-6">Unable to process your invitation</p>
              
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-4 text-left">Error Details</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="text-slate-600 font-medium text-sm w-16">Status:</div>
                    <div className="text-slate-900 font-semibold text-sm">Invalid or Expired</div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="text-slate-600 font-medium text-sm w-16">Issue:</div>
                    <div className="text-slate-900 font-semibold text-sm">{error}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
                <p className="text-slate-700 text-sm">
                  <strong>Need assistance?</strong><br />
                  This invitation link has expired or is invalid. Please contact our support team for a new invitation or assistance with your account setup.
                </p>
                <div className="mt-3">
                  <p className="text-slate-600 text-sm">
                    <strong>Contact:</strong>{' '}
                    <a href="mailto:contract@tmtit.com" className="text-indigo-600 hover:text-indigo-700 font-medium">
                      contract@tmtit.com
                    </a>
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-6 rounded-md text-sm hover:from-indigo-600 hover:to-purple-700 transition-colors duration-200 mb-4"
              >
                Sign In
              </button>
              
              <div>
                <a 
                  href="mailto:contract@tmtit.com" 
                  className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                >
                  Need help? Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden">
      <div className="flex h-full">
        {/* Left Panel */}
        <div className="w-1/2 bg-slate-700 text-white px-12 py-8 flex flex-col justify-center relative lg:px-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-lg font-bold relative">
              <span>T</span>
              <div className="absolute top-2.5 left-2.5 right-2.5 bottom-2.5 border-2 border-white rounded-full border-t-transparent border-l-transparent transform -rotate-45"></div>
            </div>
            <div className="text-2xl font-bold">METATALIO</div>
          </div>
          
          <h1 className="text-3xl font-bold mb-4">Join MetaTalio Today</h1>
          <p className="text-base text-slate-300 mb-8">Streamline your recruitment process with intelligent candidate tracking and management</p>
          
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Track candidates in real-time</h3>
                <p className="text-slate-300 text-sm">Monitor every stage of your recruitment pipeline</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">AI-powered insights</h3>
                <p className="text-slate-300 text-sm">Make data-driven hiring decisions</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Seamless collaboration</h3>
                <p className="text-slate-300 text-sm">Connect with partners effortlessly</p>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-6 left-12 text-xs text-slate-500 lg:left-16">
            © 2024 MetaTalio. All rights reserved.
          </div>
        </div>
        
        {/* Right Panel */}
        <div className="w-1/2 bg-white px-8 py-6 flex flex-col justify-center overflow-y-auto">
          <div className="max-w-sm mx-auto w-full">
            <h2 className="text-xl font-bold text-slate-900 mb-1 text-center">Create your account</h2>
            <p className="text-sm text-slate-600 mb-4 text-center">Sign up to get started with MetaTalio</p>
            
            <p className="text-sm text-slate-700 mb-1 text-center">Hello {invitationData?.fullName},</p>
            
            <p className="text-sm text-slate-600 mb-4 text-center">You have been invited to join <strong>{invitationData?.tenantName}</strong> as a <strong>Team Member</strong>. We're excited to have you on board!</p>
            
            <div className="bg-slate-50 rounded-lg p-2 mb-3">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 text-center">Your Account Details</h3>
              
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="text-slate-600 font-medium text-xs">Full Name:</div>
                  <div className="text-slate-900 font-semibold text-sm">{invitationData?.fullName || 'Loading...'}</div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-slate-600 font-medium text-xs">Email:</div>
                  <div className="text-slate-900 font-semibold text-sm">{invitationData?.email || 'Loading...'}</div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-slate-600 font-medium text-xs">Organization:</div>
                  <div className="text-slate-900 font-semibold text-sm">{invitationData?.tenantName || 'Loading...'}</div>
                </div>
              </div>
            </div>

            <form onSubmit={handleAcceptInvitation}>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-2 mb-3">
                  <p className="text-red-600 text-sm text-center">{error}</p>
                </div>
              )}

              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Create Password *</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a strong password"
                    required
                    minLength={8}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password *</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    minLength={8}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !password || !confirmPassword}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 px-4 rounded-md text-sm hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
              >
                {isSubmitting ? 'Activating Account...' : 'Activate Account'}
              </button>
            </form>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded-lg">
              <p className="text-slate-700 text-xs text-center">
                ⚠️ This invitation will expire in 24 hours. If you have any questions, please contact your administrator.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-white z-50">
          <div className="min-h-screen flex">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 flex-col justify-center relative">
              <div className="space-y-8">
                <div>
                  <h1 className="text-4xl font-bold text-white mb-4">
                    Welcome to Your AI-Powered Workspace
                  </h1>
                  <p className="text-lg text-slate-300">
                    Your account is now activated! Get ready to streamline your recruitment process with intelligent candidate tracking and management.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Track candidates in real-time</h3>
                      <p className="text-slate-400 text-sm">Monitor every stage of your recruitment pipeline</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">AI-powered insights and analytics</h3>
                      <p className="text-slate-400 text-sm">Make data-driven hiring decisions</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Seamless vendor collaboration</h3>
                      <p className="text-slate-400 text-sm">Connect with partners effortlessly</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo positioned absolutely at top */}
              <div className="absolute top-12 left-12 flex items-center space-x-3">
                <img 
                  src="/logo.svg" 
                  alt="Metatalio Logo" 
                  className="w-12 h-12"
                />
                <span className="text-2xl font-bold text-white tracking-wide">METATALIO</span>
              </div>

              {/* Footer positioned absolutely at bottom */}
              <div className="absolute bottom-12 left-12 text-slate-400 text-sm">
                © 2025, Aivaros.ai. All Rights Reserved.
              </div>
            </div>

            {/* Right Panel - Success Content */}
            <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
              <div className="w-full max-w-md text-center">
                {/* Success Icon */}
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                
                {/* Success Message */}
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Account Activated Successfully!
                </h2>
                
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Congratulations! Your account has been successfully activated. 
                  You can now access all features of MetaTalio with your new credentials.
                </p>
                
                <div className="bg-green-50 rounded-lg p-6 mb-8 text-left">
                  <h3 className="font-semibold text-slate-900 mb-3">Next steps:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-slate-700">
                      <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-white text-xs font-bold">1</span>
                      </span>
                      Sign in with your email and password
                    </li>
                    <li className="flex items-center text-slate-700">
                      <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-white text-xs font-bold">2</span>
                      </span>
                      Complete your profile setup
                    </li>
                    <li className="flex items-center text-slate-700">
                      <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-white text-xs font-bold">3</span>
                      </span>
                      Start managing your recruitment workflow
                    </li>
                  </ul>
                </div>
                
                {/* Continue Button */}
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate('/login');
                  }}
                  className="w-full bg-green-500 text-white font-semibold py-4 px-6 rounded-lg hover:bg-green-600 transition-colors duration-200 mb-4"
                >
                  Continue to Login
                </button>
                
                <p className="text-sm text-slate-500">
                  Welcome to MetaTalio - Your Complete Recruitment Management Platform
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};